import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { useGetWorkProgressListQuery } from "../../../../store/appApi";
import { selectHasModuleAccess } from "../../../../store/authSlice";

const SHIFT_BADGE = {
  morning: { label: "Morning", cls: "badge-warning light" },
  evening: { label: "Evening", cls: "badge-info light" },
};

const WorkProgressList = () => {
  const hasWpWrite = useSelector(
    selectHasModuleAccess("workProgress", "read-write"),
  );
  const { data, isLoading, isFetching, isError, refetch } =
    useGetWorkProgressListQuery();

  const reports = Array.isArray(data?.reports) ? data.reports : [];
  const loading = isLoading || isFetching;

  return (
    <>
      <PageTitle activeMenu="Progress Reports" motherMenu="Daily Progress" />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Morning Progress Reports</h4>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={refetch}
                  disabled={loading}
                  title="Refresh"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="la la-refresh" />
                  )}
                </button>
                {hasWpWrite && (
                  <Link
                    to="/progress/workprogress/add"
                    className="btn btn-primary btn-sm"
                  >
                    + New Report
                  </Link>
                )}
              </div>
            </div>

            <div className="card-body">
              {isError && (
                <div className="alert alert-danger">
                  Failed to load reports.{" "}
                  <button className="btn btn-link p-0" onClick={refetch}>
                    Retry
                  </button>
                </div>
              )}

              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              )}

              {!loading && !isError && reports.length === 0 && (
                <div className="text-center text-muted py-5">
                  <i className="la la-clipboard la-3x d-block mb-2" />
                  No progress reports yet.{" "}
                  {hasWpWrite && (
                    <Link to="/progress/workprogress/add">
                      Add the first one.
                    </Link>
                  )}
                </div>
              )}

              {!loading && reports.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered verticle-middle">
                    <thead className="thead-dark">
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Shift</th>
                        <th>Project</th>
                        <th>Added By</th>
                        <th>Locations (Staff)</th>
                        <th style={{ width: 80 }}>Material&nbsp;Lines</th>
                        <th style={{ width: 80 }}>Contractors</th>
                        <th style={{ width: 150 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((r, index) => {
                        const shift = SHIFT_BADGE[r.reportType] ?? {
                          label: r.reportType,
                          cls: "badge-secondary light",
                        };
                        return (
                          <tr key={r._id}>
                            <td>{index + 1}</td>
                            <td>
                              <span className="d-block fw-semibold">
                                {moment(r.reportDateTime).format("DD MMM YYYY")}
                              </span>
                              <small className="text-muted">
                                {moment(r.reportDateTime).format("hh:mm A")}
                              </small>
                            </td>
                            <td>
                              <span className={`badge ${shift.cls}`}>
                                {shift.label}
                              </span>
                            </td>
                            <td>
                              <span className="fw-semibold">
                                {r.projectName}
                              </span>
                              {r.projectCity && (
                                <small className="text-muted d-block">
                                  {r.projectCity}
                                </small>
                              )}
                            </td>
                            <td>{r.reportedByName}</td>
                            <td>
                              <span
                                className="text-truncate d-block"
                                style={{ maxWidth: 200 }}
                                title={r.locations}
                              >
                                {r.locations}
                              </span>
                            </td>
                            <td className="text-center">{r.materialCount}</td>
                            <td className="text-center">{r.contractorCount}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Link
                                  to={`/progress/workprogress/detail/${r._id}`}
                                  className="btn btn-info btn-xs"
                                  title="View Detail"
                                >
                                  <i className="la la-eye" />
                                </Link>
                                <Link
                                  to={`/progress/workprogress/edit/${r._id}`}
                                  className="btn btn-warning btn-xs"
                                  title="Edit"
                                >
                                  <i className="la la-pencil" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkProgressList;
